import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../../pages/Dashboard.scss";
import agentIcon from "../../assets/images/agent-icon.svg";
import launchArrow from "../../assets/images/LaunchBtnArrow.svg";
import leftArrow from "../../assets/images/ic_carousalArrowLeft.svg";
import rightArrow from "../../assets/images/ic_carousalArrowRight.svg";
import agentBg1 from "../../assets/images/agent-bg1.png";
import featuredAgent1 from "../../assets/images/featuredagent1.png";
import featuredAgent2 from "../../assets/images/featuredagent2.png";
import featuredAgent3 from "../../assets/images/featuredagent3.png";
import shelfTransparentLogo from "../../assets/images/TheShelf-transparentlogo.png";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { selectAgent } from "../../store/userSlice";
import { Agents } from "../../types/master";
import { useNavigate } from "react-router-dom";
import agentIcon1 from "../../assets/images/agent_icon3.svg";
import agentIcon2 from "../../assets/images/agent_icon2.svg";
import agentIcon3 from "../../assets/images/agent_icon1.svg";
import agentIcon4 from "../../assets/images/agent_icon4.svg";
import agentIcon5 from "../../assets/images/agent_icon1.svg";
import { v4 as uuid } from "uuid";
import { Modal } from "@mui/material";

export type Agent = {
  id: string;
  title: string;
  subtitle?: string;
};

type FeaturedAgentsProps = {
  itemsPerSlide?: number;
  onAgentClick: (agentId: number) => void;
};

const images = import.meta.glob("../../assets/images/*.png", {
  eager: true,
  as: "url",
});

const allowedAgents = [
  11, 18, 30, 27, 110, 26, 19, 7, 106, 23, 24, 21, 46, 47, 48, 20, 1, 31, 32,
  33, 44, 45, 103, 52, 54, 55, 104, 57, 58, 59,
];

// Agents that display "Coming Soon" tag
const COMING_SOON_AGENTS = ["RFP Responder"];

export default function FeaturedAgents({
  itemsPerSlide = 3,
  onAgentClick,
}: FeaturedAgentsProps): JSX.Element {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Helper function to construct video blob URL
  const getVideoUrl = useCallback((agent: Agents): string | null => {
    if (!agent.agent_intro_video_path) return null;
    return `${import.meta.env.VITE_TEMPLATE_BLOB_URL}/intro-videos/${agent.agent_intro_video_path}?${import.meta.env.VITE_TEMPLATE_G42_FILES}`;
  }, []);

  const bgColors = ["#FDE9E9", "#FDF4D9", "#F9F2FF", "#F1FCE4"];
  const imageClasses = [
    "agent-one",
    "agent-two",
    "agent-three",
    "agent-four",
    "agent-five",
  ];

  const agentIcons = [
    agentIcon1,
    agentIcon2,
    agentIcon3,
    agentIcon4,
    agentIcon5,
  ];

  // Featured agent background images - cycle through them
  const featuredAgentBgs = [featuredAgent1, featuredAgent2, featuredAgent3];

  // store
  const allAgents = useSelector((state: RootState) => state.agents.data);

  const displayedAgents = useMemo(() => {
    return allAgents
      .filter((agent) => allowedAgents.includes(agent.id))
      .sort(
        (a, b) => allowedAgents.indexOf(a.id) - allowedAgents.indexOf(b.id),
      );
  }, [allAgents]);

  // Search
  const [searchQuery, setSearchQuery] = useState("");

  const filteredAgents = useMemo(() => {
    if (!searchQuery.trim()) return displayedAgents;
    const q = searchQuery.toLowerCase();
    return displayedAgents.filter(
      (a) =>
        a.agent_name?.toLowerCase().includes(q) ||
        a.category?.toLowerCase().includes(q),
    );
  }, [displayedAgents, searchQuery]);

  // state - track current position (0 means showing first N images)
  const [currentSlide, setCurrentSlide] = useState(0);
  const [previewVideoAgentId, setPreviewVideoAgentId] = useState<number | null>(
    null,
  );
  const [openVideoAgentId, setOpenVideoAgentId] = useState<number | null>(null);

  // Responsive: 4 slides when width >= 1380px, 3 slides when < 1380px
  const [imagesPerView, setImagesPerView] = useState(
    typeof window !== "undefined" && window.innerWidth >= 1380 ? 4 : 3,
  );

  const [wrapperWidth, setWrapperWidth] = useState(0);
  const carouselWrapperRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      setImagesPerView(window.innerWidth >= 1380 ? 4 : 3);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Measure carousel wrapper width for exact slide sizing (no peek of next slide)
  useEffect(() => {
    const el = carouselWrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setWrapperWidth(el.offsetWidth);
    });
    ro.observe(el);
    setWrapperWidth(el.offsetWidth);
    return () => ro.disconnect();
  }, [imagesPerView]);

  // Clamp current slide when imagesPerView changes (e.g. 4 -> 3)
  useEffect(() => {
    const max = Math.max(0, filteredAgents.length - imagesPerView + 1);
    setCurrentSlide((prev) => (max <= 1 ? 0 : Math.min(prev, max - 1)));
  }, [imagesPerView, filteredAgents.length]);

  // Reset to first slide when search changes
  useEffect(() => {
    setCurrentSlide(0);
  }, [searchQuery]);

  // Calculate how many positions we can slide (total images - visible images + 1)
  const maxSlides = Math.max(0, filteredAgents.length - imagesPerView + 1);

  // Exact sizing when wrapper is measured
  const GAP_PX = 14;
  const cardWidthPx =
    wrapperWidth > 0
      ? (wrapperWidth - (imagesPerView - 1) * GAP_PX) / imagesPerView
      : 0;
  const slideStepPx = wrapperWidth > 0 ? cardWidthPx + GAP_PX : 0;
  const listWidthPx =
    wrapperWidth > 0 && filteredAgents.length > 0
      ? filteredAgents.length * cardWidthPx +
        (filteredAgents.length - 1) * GAP_PX
      : 0;
  const useExactSizing = wrapperWidth > 0 && listWidthPx > 0;
  const maxTranslatePx = Math.max(0, listWidthPx - wrapperWidth);
  const translatePx = useExactSizing
    ? Math.min(currentSlide * slideStepPx, maxTranslatePx)
    : 0;

  useEffect(() => {
    if (maxSlides <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev >= maxSlides - 1 ? 0 : prev + 1));
    }, 12000);

    return () => clearInterval(interval);
  }, [maxSlides]);

  const nextSlide = () => {
    if (currentSlide < maxSlides - 1) {
      setCurrentSlide((prev) => prev + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide((prev) => prev - 1);
    }
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const handleSelectAgent = useCallback(
    (agent: Agents) => {
      onAgentClick(agent.id);
    },
    [onAgentClick],
  );

  // Helper function to check if agent is available for launch
  const isAgentLaunchable = useCallback((agent: Agents): boolean => {
    const EXTERNAL_ENDPOINT_AGENTS = [19, 31, 32, 33, 34, 53, 56];
    if (EXTERNAL_ENDPOINT_AGENTS.includes(agent.id) && !agent.endpoint) {
      return false;
    }
    return true;
  }, []);

  const handleVideoIconClick = (agent: Agents) => {
    setOpenVideoAgentId(agent.id);
  };

  const closeVideoPopover = () => {
    setOpenVideoAgentId(null);
  };

  const openVideoUrl = useMemo(() => {
    if (openVideoAgentId === null) return null;
    const selectedAgent = displayedAgents.find(
      (a) => a.id === openVideoAgentId,
    );
    return selectedAgent ? getVideoUrl(selectedAgent) : null;
  }, [openVideoAgentId, displayedAgents, getVideoUrl]);

  return (
    <section className="featured">
      {/* Header row: title + search */}
      <div className="featured-header">
        <h2 className="featured-title">Featured Agents</h2>
        <div className="featured-search-wrapper">
          <svg
            className="featured-search-icon"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="8.5"
              cy="8.5"
              r="5.5"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d="M13 13L17 17"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <input
            type="text"
            className="featured-search-input"
            placeholder="Search agents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="featured-search-clear"
              onClick={() => setSearchQuery("")}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="carousel-container">
        <div className="carousel-wrapper" ref={carouselWrapperRef}>
          <div
            className="featured-list"
            style={{
              ...(useExactSizing && { width: listWidthPx }),
              transform: useExactSizing
                ? `translateX(-${translatePx}px)`
                : `translateX(calc(-${currentSlide} * (100% / ${imagesPerView})))`,
            }}
          >
            {filteredAgents.map((a: Agents, index) => (
              <div
                key={a.id}
                className={`agent-card ${imageClasses[index % 5]}`}
                style={{
                  backgroundImage: `url(${featuredAgentBgs[index % 3]})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  ...(useExactSizing
                    ? {
                        width: cardWidthPx,
                        minWidth: cardWidthPx,
                        flex: `0 0 ${cardWidthPx}px`,
                      }
                    : {
                        width: `calc((100% - ${(imagesPerView - 1) * GAP_PX}px) / ${imagesPerView})`,
                        minWidth: `calc((100% - ${(imagesPerView - 1) * GAP_PX}px) / ${imagesPerView})`,
                        flex: `0 0 calc((100% - ${(imagesPerView - 1) * GAP_PX}px) / ${imagesPerView})`,
                      }),
                }}
              >
                {getVideoUrl(a) && (
                  <>
                    <div className="agent-video-icon-wrapper">
                      <span className="agent-sneak-peek-text">
                        Watch it work
                      </span>
                      <button
                        className="agent-video-icon"
                        onMouseEnter={() => setPreviewVideoAgentId(a.id)}
                        onMouseLeave={() =>
                          setPreviewVideoAgentId((prev) =>
                            prev === a.id ? null : prev,
                          )
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVideoIconClick(a);
                        }}
                        aria-label="Preview agent video"
                      >
                        ▶
                      </button>
                    </div>

                    {previewVideoAgentId === a.id && (
                      <div className="agent-video-preview">
                        <video
                          src={getVideoUrl(a)!}
                          autoPlay
                          muted
                          loop
                          playsInline
                        />
                      </div>
                    )}
                  </>
                )}

                <div className="agent-image"></div>
                <div className="agent-content-wrapper">
                  <div className="agent-content-left">
                    <img
                      src={shelfTransparentLogo}
                      alt="The Shelf Logo"
                      className="shelf-logo"
                    />
                  </div>
                  <div className="agent-content-right">
                    <div className="agent-title">
                      {a.agent_name}
                      {a.agent_name === "Interviewer Host" && (
                        <div className="agent-sovereign-tag">
                          Sovereign version coming soon
                        </div>
                      )}
                      {a.agent_name ===
                        "Contract Review and Finalization Agent" && (
                        <div className="agent-sovereign-tag">Pre Beta</div>
                      )}
                      {a.agent_name === "AI Agent Screener" && (
                        <div className="agent-sovereign-tag">Pre Beta</div>
                      )}
                      {a.agent_name ===
                        "Contract Request (SOW Creator) Agent" && (
                        <div className="agent-sovereign-tag">Pre Beta</div>
                      )}
                      {a.agent_name === "Template Review (MSA) Agent" && (
                        <div className="agent-sovereign-tag">Pre Beta</div>
                      )}
                      {a.id === 48 && (
                        <div className="agent-sovereign-tag">Pre Beta</div>
                      )}
                      {a.id === 110 && (
                        <div className="agent-sovereign-tag">Pre Beta</div>
                      )}
                      {a.id === 44 && (
                        <div className="agent-sovereign-tag">Pre Beta</div>
                      )}
                      {a.id === 47 && (
                        <div className="agent-sovereign-tag">Pre Beta</div>
                      )}
                      {a.id === 26 && (
                        <div className="agent-sovereign-tag">Pre Beta</div>
                      )}
                      {a.id === 45 && (
                        <div className="agent-sovereign-tag">Pre Beta</div>
                      )}
                      {a.id === 54 && (
                        <div className="agent-sovereign-tag">Pre Beta</div>
                      )}
                      {COMING_SOON_AGENTS.includes(a.agent_name) && (
                        <div className="agent-sovereign-tag">Coming Soon</div>
                      )}
                    </div>
                    <div className="agent-left">
                      <div className="agent-sub">{a.category}</div>
                      {!COMING_SOON_AGENTS.includes(a.agent_name) && (
                        <button
                          className="launch"
                          onClick={() => handleSelectAgent(a)}
                          disabled={!isAgentLaunchable(a)}
                          style={{
                            opacity: !isAgentLaunchable(a) ? 0.5 : 1,
                            cursor: !isAgentLaunchable(a)
                              ? "not-allowed"
                              : "pointer",
                          }}
                        >
                          <img
                            src={launchArrow}
                            alt="Agent"
                            className="agentArrow"
                          />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {filteredAgents.length === 0 && searchQuery && (
        <div className="featured-no-results">
          No agents match &ldquo;{searchQuery}&rdquo;
        </div>
      )}

      {maxSlides > 1 && (
        <div className="carousel-controls">
          <button
            className="carousel-btn carousel-btn-prev"
            onClick={prevSlide}
            aria-label="Previous slide"
            disabled={currentSlide === 0}
          >
            <img src={leftArrow} alt="Previous" />
          </button>

          <div className="carousel-dots">
            {Array.from({ length: maxSlides }).map((_, index) => (
              <button
                key={index}
                className={`carousel-dot ${
                  index === currentSlide ? "active" : ""
                }`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          <button
            className="carousel-btn carousel-btn-next"
            onClick={nextSlide}
            aria-label="Next slide"
            disabled={currentSlide >= maxSlides - 1}
          >
            <img src={rightArrow} alt="Next" />
          </button>
        </div>
      )}

      {openVideoUrl && (
        <Modal
          open={true}
          onClose={closeVideoPopover}
          className="agent-video-modal"
          aria-labelledby="agent-video-modal-title"
          aria-describedby="agent-video-modal-description"
          slotProps={{
            backdrop: {
              sx: { backgroundColor: "rgba(0, 0, 0, 0.6)" },
            },
          }}
        >
          <div className="agent-video-modal-content">
            <button
              className="agent-video-modal-close"
              onClick={(e) => {
                e.stopPropagation();
                closeVideoPopover();
              }}
              aria-label="Close video modal"
            >
              ✕
            </button>
            <video
              src={openVideoUrl}
              controls
              autoPlay
              className="agent-video-modal-player"
            />
          </div>
        </Modal>
      )}
    </section>
  );
}
